import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  FileText,
  LogOut,
  Menu,
  X,
  Warehouse,
  Truck,
  Receipt,
  User,
  Image,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Wallet,
  UserPlus,
  Store,
  ClipboardList,
  Settings,
  MapPin,
  Bike,
  Award,
  Shield,
  Building2,
  Plug,
  Trophy,
  MessageCircle,
} from "lucide-react";

// Menú optimizado y reorganizado para el trabajo diario
const mainNavItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
];

// Sección de Operaciones (lo más usado día a día)
const operacionesItems = [
  { to: "/admin/pedidos-online", icon: ClipboardList, label: "Pedidos Online" },
  { to: "/admin/despachos", icon: Truck, label: "Despachos" },
];

// Sección de Comercial
const comercialItems = [
  { to: "/admin/catalogo", icon: Store, label: "Catálogo Digital" },
  { to: "/admin/clientes", icon: Users, label: "Clientes (CRM)" },
  { to: "/admin/club", icon: Trophy, label: "Club de Fidelidad" },
  { to: "/admin/club-whatsapp", icon: MessageCircle, label: "Club WhatsApp" },
  { to: "/admin/productos", icon: Package, label: "Productos" },
  { to: "/admin/inventario", icon: Warehouse, label: "Inventario" },
  { to: "/admin/aperturas", icon: Warehouse, label: "Apertura Diaria" },
];

// Sección de Equipo
const equipoItems = [
  { to: "/admin/vendedores", icon: Users, label: "Vendedores" },
  { to: "/admin/mayoristas", icon: Building2, label: "Mayoristas" },
  { to: "/admin/domiciliarios", icon: Bike, label: "Domiciliarios" },
  { to: "/admin/embajadores", icon: Award, label: "Embajadores" },
  { to: "/admin/solicitudes", icon: UserPlus, label: "Solicitudes" },
];

// Reportes y Finanzas
const reportesSubItems = [
  { to: "/admin/ventas", icon: ShoppingCart, label: "Ventas" },
  { to: "/admin/nomina", icon: Wallet, label: "Nómina y Pagos" },
  { to: "/admin/incentivos", icon: Trophy, label: "Comisiones e Incentivos" },
  { to: "/admin/gastos", icon: Receipt, label: "Gastos" },
  { to: "/admin/reportes", icon: BarChart3, label: "Dashboard Reportes" },
];

// Configuración
const configItems = [
  { to: "/admin/capacidades", icon: Shield, label: "Perfiles y Capacidades" },
  { to: "/admin/integraciones", icon: Plug, label: "Integraciones / API" },
  { to: "/admin/zonas-envio", icon: MapPin, label: "Zonas de Envío" },
  { to: "/admin/config-landing", icon: Image, label: "Config Landing" },
  { to: "/admin/requisiciones", icon: Package, label: "Requisiciones" },
  { to: "/admin/perfil", icon: User, label: "Mi Perfil" },
];

// Componente de sección de menú
const MenuSection = ({ title, items, isOpen, onToggle, sidebarOpen, setSidebarOpen }) => {
  return (
    <div className="pt-2">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-2 text-xs font-semibold text-[#4A2C0A]/60 uppercase tracking-wider hover:text-[#4A2C0A] transition-colors"
      >
        {title}
        {isOpen ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
      </button>
      
      {isOpen && (
        <div className="space-y-1 mt-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#4A2C0A] text-white"
                    : "text-[#4A2C0A] hover:bg-white/50"
                }`
              }
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Secciones colapsables - operaciones siempre abierto por defecto
  const [operacionesOpen, setOperacionesOpen] = useState(true);
  const [comercialOpen, setComercialOpen] = useState(true);
  const [equipoOpen, setEquipoOpen] = useState(false);
  const [reportesOpen, setReportesOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FBF5E8]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E5D4B3] z-40 flex items-center justify-between px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-[#FBF5E8] rounded-lg"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X className="w-6 h-6 text-[#4A2C0A]" /> : <Menu className="w-6 h-6 text-[#4A2C0A]" />}
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#C8902A] rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-bold">B</span>
          </div>
          <span className="font-['Playfair_Display'] text-[#4A2C0A] font-semibold">Admin</span>
        </div>
        <NotificationBell />
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-64 bg-[#F5EBD7] border-r border-[#E5D4B3] z-50 transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-[#E5D4B3]">
            <div className="flex items-center gap-3">
              <img
                src="/logo-torta-cuajada.png"
                alt="Logo"
                className="w-10 h-10 rounded-full object-cover"
              />
              <div>
                <h1 className="font-['Playfair_Display'] text-lg text-[#4A2C0A] font-bold leading-tight">Baruj Shefa</h1>
                <p className="text-[10px] text-[#C8902A] uppercase tracking-wider font-semibold">Administrador</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-2 overflow-y-auto">
            {/* Dashboard */}
            {mainNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-[#4A2C0A] text-white"
                      : "text-[#4A2C0A] hover:bg-white/50"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}

            {/* Operaciones */}
            <MenuSection
              title="Operaciones"
              items={operacionesItems}
              isOpen={operacionesOpen}
              onToggle={() => setOperacionesOpen(!operacionesOpen)}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />

            {/* Comercial */}
            <MenuSection
              title="Comercial"
              items={comercialItems}
              isOpen={comercialOpen}
              onToggle={() => setComercialOpen(!comercialOpen)}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />

            {/* Equipo */}
            <MenuSection
              title="Equipo"
              items={equipoItems}
              isOpen={equipoOpen}
              onToggle={() => setEquipoOpen(!equipoOpen)}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />

            {/* Reportes */}
            <MenuSection
              title="Reportes y Finanzas"
              items={reportesSubItems}
              isOpen={reportesOpen}
              onToggle={() => setReportesOpen(!reportesOpen)}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />

            {/* Configuración */}
            <MenuSection
              title="Configuración"
              items={configItems}
              isOpen={configOpen}
              onToggle={() => setConfigOpen(!configOpen)}
              sidebarOpen={sidebarOpen}
              setSidebarOpen={setSidebarOpen}
            />
          </nav>

          {/* User Section */}
          <div className="p-3 border-t border-[#E5D4B3]">
            <div className="flex items-center justify-between mb-2 px-2">
              <div className="min-w-0">
                <p className="font-medium text-[#4A2C0A] text-sm truncate">{user?.nombre}</p>
                <p className="text-xs text-[#4A2C0A]/60 truncate">{user?.email}</p>
              </div>
              <NotificationBell />
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="w-full justify-start border-[#E5D4B3] text-[#4A2C0A] hover:bg-[#4A2C0A] hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
